/**
 * Core Solidity type system and parameter tree structures
 *
 * These types are shared across:
 * - Transaction builder (frontend form state)
 * - Transaction display (frontend rendering)
 * - Transaction decoder (backend/package)
 * - Database storage (JSON serialization)
 */

import { maxUint256 } from 'viem';

/** Return whether a decimal integer string fits in an EVM uint256. */
export function isUint256(value: string): boolean {
  // Case: Reject non-decimal or oversized values before invoking BigInt parsing.
  if (!/^\d+$/.test(value) || value.length > maxUint256.toString().length) {
    return false;
  }

  try {
    return BigInt(value) <= maxUint256;
  } catch {
    return false;
  }
}

/**
 * Higher-level Solidity type classifications for routing display logic
 */
export enum HighLevelSolidityType {
  ARRAY = 'ARRAY',
  STRUCT_OR_TUPLE = 'STRUCT_OR_TUPLE',
  NUMBER = 'NUMBER',
  ADDRESS = 'ADDRESS',
  PRIMITIVE = 'PRIMITIVE',
  STRING = 'STRING',
  BYTES = 'BYTES',
  BOOLEAN = 'BOOLEAN',
}

/**
 * A node describing a Solidity parameter
 *
 * Because of the unpredictable nature of solidity type nesting,
 * Solidity parameters are represented as trees, where each node in the tree
 * describes a parameter or nested parameter.
 *
 * This structure preserves the ABI details needed for display rendering.
 */
export interface ParamTreeNode {
  highLevelType: HighLevelSolidityType;
  children?: ParamTreeNode[];
  structParamName?: string;
  internalType?: string;
  rawType: string;
  rawValue?: string;
}

/**
 * Parsed parameter value type - recursive union for nested arrays/tuples
 *
 * Represents a parameter value after validation and parsing:
 * - Primitives: string (addresses, bytes), boolean
 * - Complex: recursive arrays of ParameterParsedValue
 *
 * Used in transaction builder state and backend storage.
 */
export type ParameterParsedValue = string | boolean | ParameterParsedValue[];

/**
 * Raw parameter value type - supports BigInt for large numbers
 *
 * Used during decoding and display to handle:
 * - Very large integers (uint256) that exceed Number.MAX_SAFE_INTEGER
 * - Nested arrays of raw values
 *
 * Backend converts BigInt to string for JSON serialization.
 */
export type RawParamValue =
  | string
  | number
  | boolean
  | bigint
  | RawParamValue[];
